import { grey } from '@mui/material/colors';
import { type PaletteMode } from '@mui/material/styles';
import {
  darken,
  emphasize,
  getContrastRatio,
  lighten,
} from '@mui/system/colorManipulator';

export interface BrandColors {
  primary: string;
  secondary: string;
}

const MODE_BACKGROUND: Record<PaletteMode, string> = {
  light: '#ffffff',
  dark: '#121212',
};

function hasContrastOnBackground(color: string, mode: PaletteMode): boolean {
  try {
    return getContrastRatio(color, MODE_BACKGROUND[mode]) >= 4.5;
  } catch {
    return false;
  }
}

function ensureContrastOnBackground(color: string, mode: PaletteMode): string {
  if (hasContrastOnBackground(color, mode)) {
    return color;
  }
  const adjusted = emphasize(color, mode === 'dark' ? 0.45 : 0.55);
  if (hasContrastOnBackground(adjusted, mode)) {
    return adjusted;
  }
  const fallbacks =
    mode === 'dark'
      ? [grey[300], grey[400], '#ffffff']
      : [grey[700], grey[800], '#000000'];
  return (
    fallbacks.find((candidate) => hasContrastOnBackground(candidate, mode)) ??
    (mode === 'dark' ? grey[300] : grey[800])
  );
}

export function resolvePrimaryMain(
  brandColors: BrandColors,
  mode: PaletteMode
): string {
  const { primary, secondary } = brandColors;
  if (hasContrastOnBackground(primary, mode)) {
    return primary;
  }
  if (hasContrastOnBackground(secondary, mode)) {
    return secondary;
  }
  return ensureContrastOnBackground(primary, mode);
}

export function resolveSecondaryMain(
  brandColors: BrandColors,
  mode: PaletteMode
): string {
  const { primary, secondary } = brandColors;
  if (hasContrastOnBackground(secondary, mode)) {
    return secondary;
  }
  const fromPrimary =
    mode === 'dark' ? lighten(primary, 0.35) : darken(primary, 0.25);
  if (hasContrastOnBackground(fromPrimary, mode)) {
    return fromPrimary;
  }
  return ensureContrastOnBackground(
    mode === 'dark' ? grey[400] : grey[700],
    mode
  );
}
